import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material";
import { dark, light } from "@mui/material/styles/createPalette";
import { GridToolbarContainer } from "@mui/x-data-grid";
import { MusicVideoOutlined } from "@mui/icons-material";

// Color design token
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        white: "#ffffff",
        primary: {
          100: "#EBF1FE",
          200: "#D7E2FC",
          300: "#B5CAFA",
          400: "#366EF0",
        },
        secondary: {
          100: "#F1F1F1",
          200: "#CFCFCF",
          300: "#969498",
          400: "#2F2A32",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
        hover: {
          main: "#2a2d64",
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        white: "#ffffff",
        primary: {
          100: "#EBF1FE",
          200: "#D7E2FC",
          300: "#B5CAFA",
          400: "#366EF0",
        },
        secondary: {
          100: "#F1F1F1",
          200: "#CFCFCF",
          300: "#969498",
          400: "#2F2A32",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
        hover: {
          main: "#f0f0f0", // works well in light mode
        },
      }),
});

// mui them setting
export const themeSettings = (mode) => {
  const colors = tokens(mode);

  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[400],
              light: colors.primary[200],
              dark: colors.primary[300],
              contrastText: colors.white,
            },
            secondary: {
              main: colors.secondary[400],
              light: colors.secondary[200],
              dark: colors.secondary[300],
              contrastText: colors.white,
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.white,
              paper: colors.white,
            },
            text: {
              primary: colors.secondary[400],
              secondary: colors.secondary[300],
            },
            divider: colors.primary[400],
            action: {
              hover: colors.secondary[100],
              selected: colors.secondary[200],
              disabled: colors.secondary[200],
              disabledBackground: colors.secondary[100],
            },
            redAccent: {
              main: colors.redAccent[500],
              light: colors.redAccent[400],
              dark: colors.redAccent[600],
            },
            blueAccent: {
              main: colors.blueAccent[500],
            },
          }
        : {
            primary: {
              main: colors.primary[400],
              light: colors.primary[200],
              dark: colors.primary[300],
              contrastText: colors.white,
            },
            secondary: {
              main: colors.secondary[400],
              light: colors.secondary[200],
              dark: colors.secondary[300],
              contrastText: colors.white,
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.white,
              paper: colors.white,
            },
            text: {
              primary: colors.secondary[400],
              secondary: colors.secondary[300],
            },
            divider: colors.primary[400],
            action: {
              hover: colors.secondary[100],
              selected: colors.secondary[200],
              disabled: colors.secondary[200],
              disabledBackground: colors.secondary[100],
            },
            redAccent: {
              main: colors.redAccent[500],
              light: colors.redAccent[400],
              dark: colors.redAccent[600],
            },
            blueAccent: {
              main: colors.blueAccent[500],
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans 3", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 40,
        fontWeight: 700,
      },
      h2: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 32,
        fontWeight: 700,
      },
      h3: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 24,
        fontWeight: 700,
      },
      h4: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 20,
        fontWeight: 700,
      },
      h5: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 16,
        fontWeight: 700,
      },
      h6: {
        fontFamily: ["Source Sans 3", "sans-serif"].join(","),
        fontSize: 14,
        fontWeight: 700,
      },
    },

    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            "--DataGrid-borderColor": colors.primary[400],
            backgroundColor: colors.white,
            borderRadius: "8px",
          },
          columnHeaderTitle: {
            fontWeight: 700,
            fontSize: "14px",
          },
          columnHeaders: {
            backgroundColor: colors.primary[100],
            borderBottom: `1px solid ${colors.primary[400]}`,
            color: colors.secondary[400],
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          },

          cell: {
            borderBottom: `1px solid ${colors.primary[200]}`,
            color: colors.secondary[400],
            fontSize: "14px",
          },

          footerContainer: {
            borderTop: `1px solid ${colors.primary[400]}`,
            backgroundColor: colors.primary[100],
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          },

          toolbarContainer: {
            // backgroundColor: colors.primary[100],
            padding: "5px",
            gap: "10px",
            "& button": {
              color: colors.grey[100],
            },
          },

          row: {
            "&:hover": {
              backgroundColor: colors.primary[100],
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "6px",
            px: 2.5,
            py: 1,
            boxShadow: "none",
            fontSize: ".875rem",
            // marginTop: "15px",

            "&:hover": {
              backgroundColor: "var(--color-primary-200)",
              boxShadow: "none",
              color: "var(--color-primary-400)",
            },
          },
          sizeSmall: {
            padding: "10px",
          },
        },
      },
    },
  };
};

// Context for the color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    [],
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return [theme, colorMode];
};
